<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="headline">
            דוחות מצב - יישובים
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              @click="downloadCSV"
              :disabled="loading || reports.length === 0"
              class="ml-2"
            >
              <v-icon left>mdi-download</v-icon>
              הורד CSV
            </v-btn>
            <v-btn color="error" @click="clearAllReports" :loading="clearing">
              <v-icon left>mdi-delete</v-icon>
              נקה הכל
            </v-btn>
          </v-card-title>

          <v-card-text>
            <div v-if="loading" class="text-center pa-4">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
              <div class="mt-2">טוען נתונים...</div>
            </div>

            <div v-else-if="error" class="text-center pa-4">
              <v-alert type="error">{{ error }}</v-alert>
            </div>

            <div v-else>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-right team-header">צוות/מיקום</th>
                      <th
                        v-for="department in departments"
                        :key="department.category"
                        class="text-center"
                        :style="{ backgroundColor: department.color }"
                      >
                        {{ department.text }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="team in teams" :key="team._id" class="team-row">
                      <td class="font-weight-bold team-name-cell">
                        {{ team.name }}
                      </td>
                      <td
                        v-for="department in departments"
                        :key="department.category"
                        class="text-center"
                      >
                        <div
                          v-if="
                            getReportContent(team.name, department.category)
                          "
                        >
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <div
                                v-bind="attrs"
                                v-on="on"
                                class="report-cell"
                                @click="
                                  openResponseDialog(
                                    team.name,
                                    department.category
                                  )
                                "
                              >
                                {{
                                  truncateText(
                                    getReportContent(
                                      team.name,
                                      department.category
                                    ),
                                    50
                                  )
                                }}
                                <v-icon
                                  v-if="
                                    getReportResponse(
                                      team.name,
                                      department.category
                                    )
                                  "
                                  small
                                  color="success"
                                  class="float-left mt-1"
                                  >mdi-message-reply-text</v-icon
                                >
                              </div>
                            </template>
                            <div class="tooltip-content">
                              <div>
                                <strong>תוכן:</strong>
                                {{
                                  getReportContent(
                                    team.name,
                                    department.category
                                  )
                                }}
                              </div>
                              <div>
                                <strong>משתמש:</strong>
                                {{
                                  getReportUsername(
                                    team.name,
                                    department.category
                                  )
                                }}
                              </div>
                              <div>
                                <strong>זמן:</strong>
                                {{
                                  formatTime(
                                    getReportTime(
                                      team.name,
                                      department.category
                                    )
                                  )
                                }}
                              </div>
                              <div
                                v-if="
                                  getReportResponse(
                                    team.name,
                                    department.category
                                  )
                                "
                                class="mt-2 pt-2 border-top"
                              >
                                <strong>תגובה:</strong>
                                {{
                                  getReportResponse(
                                    team.name,
                                    department.category
                                  )
                                }}
                                <div class="caption">
                                  ע"י
                                  {{
                                    getReportResponseBy(
                                      team.name,
                                      department.category
                                    )
                                  }}
                                </div>
                              </div>
                            </div>
                          </v-tooltip>
                        </div>
                        <div v-else class="text--disabled">אין דוח</div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div class="mt-4 text-caption text--secondary">
                עדכון אחרון: {{ lastUpdate }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="responseDialog" max-width="500px">
      <v-card>
        <v-card-title>תגובה לדוח</v-card-title>
        <v-card-text>
          <div class="mb-2" v-if="selectedReport">
            <strong
              >דוח מקורי ({{ selectedReport.teamName }} -
              {{ getCategoryText(selectedReport.department) }}):</strong
            >
            <div class="grey--text text--darken-2 mt-1 pa-2 bg-light rounded">
              {{ selectedReport.content }}
            </div>
          </div>
          <v-textarea
            v-model="responseText"
            label="תגובה"
            outlined
            rows="3"
            class="mt-4"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="responseDialog = false">ביטול</v-btn>
          <v-btn
            color="primary"
            @click="submitResponse"
            :loading="submittingResponse"
            >שלח תגובה</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import myAxios from '@/services/myAxios'
import { categories } from '@/helpers/categoriesAndTypes'

export default {
  name: 'CouncilReportsAdmin',
  data() {
    return {
      loading: true,
      clearing: false,
      error: null,
      teams: [],
      departments: categories,
      reports: [],
      lastUpdate: null,
      responseDialog: false,
      selectedReport: null,
      responseText: '',
      submittingResponse: false,
    }
  },

  sockets: {
    'council-report-received'(data) {
      console.log('Received council report update:', data)
      this.updateReport(data)
      this.lastUpdate = new Date().toLocaleString('he-IL')
    },
    'council-report-response-received'(data) {
      console.log('Received council report response:', data)
      this.updateReportResponse(data)
    },
  },

  async mounted() {
    await this.fetchData()
  },

  methods: {
    async fetchData() {
      this.loading = true
      this.error = null

      try {
        // Fetch teams, categories (already have), and reports in parallel
        const [teamsResponse, reportsResponse] = await Promise.all([
          myAxios.get('/team'),
          myAxios.get('/council-report'),
        ])

        this.teams = teamsResponse.data
        this.reports = reportsResponse.data
        this.lastUpdate = new Date().toLocaleString('he-IL')
      } catch (error) {
        console.error('Error fetching data:', error)
        this.error = 'שגיאה בטעינת הנתונים'
      } finally {
        this.loading = false
      }
    },

    updateReport(newReport) {
      // Remove existing report for the same team/department
      this.reports = this.reports.filter(
        report =>
          !(
            report.teamName === newReport.teamName &&
            report.department === newReport.department
          )
      )

      // Add the new report
      this.reports.push({
        teamName: newReport.teamName,
        department: newReport.department,
        content: newReport.content,
        username: newReport.username,
        createdAt: newReport.timestamp || new Date().toISOString(),
        // New report has no response yet
        response: null,
        responseBy: null,
        responseTime: null,
      })
    },

    updateReportResponse(data) {
      const report = this.reports.find(
        r => r.teamName === data.teamName && r.department === data.department
      )
      if (report) {
        // Use Vue.set or re-assign to ensure reactivity if needed, though direct assignment usually works in Vue 2 for existing props
        report.response = data.response
        report.responseBy = data.responseBy
        report.responseTime = data.responseTime
        // Force update if needed, but array mutation usually triggers
        this.reports = [...this.reports]
      }
    },

    getReportContent(teamName, department) {
      const report = this.reports.find(
        r => r.teamName === teamName && r.department === department
      )
      return report ? report.content : null
    },

    getReportUsername(teamName, department) {
      const report = this.reports.find(
        r => r.teamName === teamName && r.department === department
      )
      return report ? report.username : null
    },

    getReportTime(teamName, department) {
      const report = this.reports.find(
        r => r.teamName === teamName && r.department === department
      )
      return report ? report.createdAt : null
    },

    getReportResponse(teamName, department) {
      const report = this.reports.find(
        r => r.teamName === teamName && r.department === department
      )
      return report ? report.response : null
    },

    getReportResponseBy(teamName, department) {
      const report = this.reports.find(
        r => r.teamName === teamName && r.department === department
      )
      return report ? report.responseBy : null
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength
        ? text.substring(0, maxLength) + '...'
        : text
    },

    formatTime(timestamp) {
      if (!timestamp) return ''
      return new Date(timestamp).toLocaleString('he-IL')
    },

    getCategoryText(categoryKey) {
      const cat = this.departments.find(d => d.category === categoryKey)
      return cat ? cat.text : categoryKey
    },

    openResponseDialog(teamName, department) {
      const report = this.reports.find(
        r => r.teamName === teamName && r.department === department
      )
      if (report) {
        this.selectedReport = report
        this.responseText = report.response || ''
        this.responseDialog = true
      }
    },

    async submitResponse() {
      if (!this.selectedReport) return

      this.submittingResponse = true
      try {
        const payload = {
          teamName: this.selectedReport.teamName,
          department: this.selectedReport.department,
          response: this.responseText,
          responseBy: this.$store.getters.username || 'Admin',
        }

        await myAxios.post('/council-report/response', payload)

        // Update local state
        this.updateReportResponse({
          ...payload,
          responseTime: new Date(),
        })

        // Emit socket event so other admins and the user see it
        this.$socket.client.emit('council-report-response', payload)

        this.responseDialog = false
        this.$infoToast('תגובה נשלחה בהצלחה')
      } catch (e) {
        console.error(e)
        this.$errorToast('שגיאה בשליחת תגובה')
      } finally {
        this.submittingResponse = false
      }
    },

    downloadCSV() {
      // Create CSV content
      const csvContent = this.generateCSV()

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      link.setAttribute('href', url)
      link.setAttribute(
        'download',
        `council_reports_${new Date().toISOString().split('T')[0]}.csv`
      )
      link.style.visibility = 'hidden'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$infoToast('קובץ CSV הורד בהצלחה')
    },

    generateCSV() {
      // Create header row
      const headers = ['צוות/מיקום', ...this.departments.map(d => d.text)]
      let csv = headers.join(',') + '\n'

      // Add data rows
      this.teams.forEach(team => {
        const row = [team.name]

        this.departments.forEach(department => {
          const content = this.getReportContent(team.name, department.category)
          const username = this.getReportUsername(
            team.name,
            department.category
          )
          const time = this.getReportTime(team.name, department.category)
          const response = this.getReportResponse(
            team.name,
            department.category
          )

          if (content) {
            let cellText = `${content} | ${username} | ${this.formatTime(time)}`
            if (response) {
              cellText += ` | תגובה: ${response}`
            }
            // Escape quotes and wrap in quotes if contains comma
            const cellValue = `"${cellText.replace(/"/g, '""')}"`
            row.push(cellValue)
          } else {
            row.push('אין דוח')
          }
        })

        csv += row.join(',') + '\n'
      })

      return csv
    },

    async clearAllReports() {
      const { isConfirmed } = await this.$createSwal({
        title: 'האם אתה בטוח שאתה רוצה למחוק? מחיקה היא פעולה בלתי הפיכה',
        confirmButtonText: this.$t('generics.erase'),
      })
      if (!isConfirmed) return

      this.clearing = true

      try {
        await myAxios.delete('/council-report')
        this.reports = []
        this.lastUpdate = new Date().toLocaleString('he-IL')
        this.$infoToast('כל הדוחות נמחקו בהצלחה')
      } catch (error) {
        console.error('Error clearing reports:', error)
        this.$errorToast('שגיאה במחיקת הדוחות')
      } finally {
        this.clearing = false
      }
    },
  },
}
</script>

<style scoped>
.report-cell {
  max-width: 200px;
  word-break: break-word;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin: 2px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.report-cell:hover {
  background-color: #e0e0e0;
}

.tooltip-content {
  max-width: 300px;
  white-space: pre-wrap;
}

.border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}

.bg-light {
  background-color: #f5f5f5;
}

th {
  white-space: nowrap;
}

.v-simple-table th,
.v-simple-table td {
  padding: 8px !important;
}

.team-header {
  background-color: #1976d2 !important;
  color: white !important;
  font-weight: bold !important;
  font-size: 16px !important;
  border-right: 3px solid #0d47a1;
  position: sticky;
  right: 0;
  z-index: 10;
}

.team-name-cell {
  background-color: #e3f2fd !important;
  color: #1976d2 !important;
  font-weight: bold !important;
  font-size: 14px !important;
  border-right: 3px solid #1976d2;
  position: sticky;
  right: 0;
  z-index: 5;
  min-width: 120px;
}

.team-name-cell:hover {
  background-color: #bbdefb !important;
}

.team-row:hover .team-name-cell {
  background-color: #90caf9 !important;
  transform: scale(1.02);
  transition: all 0.2s ease;
}
</style>
